import { useEffect, useRef, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ArrowDownTrayIcon, DocumentIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline'
import apiClient from '../services/apiClient'
import { useAuthStore } from '../store/authStore'
import { UserRole } from '../types/auth'
import DocumentViewer from '../components/admin/DocumentViewer'
import FreehandWhiteboard, { FreehandWhiteboardHandle } from '../components/learning/FreehandWhiteboard'

// ─── Types ────────────────────────────────────────────────────────────────────
interface WorkshopMaterial {
  id: number
  title: string
  file_url: string
  file_type: string
  file_size: number | null
  position: number
}

interface WorkshopWhiteboard {
  id: number
  title: string
  diagram_id: string
  png_url: string | null
  xml_url: string | null
  is_published?: number
  position: number
}

interface WorkshopDetail {
  id: number
  slug: string
  title: string
  description: string | null
  thumbnail_url: string | null
  held_at: string
  duration_min: number | null
  youtube_url: string | null
  status: 'upcoming' | 'completed'
  materials: WorkshopMaterial[]
  whiteboards: WorkshopWhiteboard[]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(dateStr: string) {
  try {
    const d = new Date(dateStr)
    return d.toLocaleDateString('vi-VN', {
      weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return dateStr }
}

function fileIcon(type: string) {
  const t = type.toLowerCase()
  if (t === 'pdf') return '📕'
  if (t === 'html' || t === 'htm') return '🌐'
  if (t === 'pptx' || t === 'ppt') return '📊'
  if (t === 'docx' || t === 'doc') return '📝'
  return '📄'
}

function defaultWbTitle() {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `Bảng ${pad(d.getHours())}:${pad(d.getMinutes())} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}`
}

// ─── YouTube Embed ─────────────────────────────────────────────────────────────
function extractYouTubeId(url: string): string | null {
  try {
    const u = new URL(url)
    if (u.hostname.includes('youtu.be')) return u.pathname.slice(1).split('?')[0]
    const v = u.searchParams.get('v')
    if (v) return v
    const match = u.pathname.match(/(?:embed|v)\/([^/?]+)/)
    return match ? match[1] : null
  } catch { return null }
}

function YouTubeEmbed({ url }: { url: string }) {
  const videoId = extractYouTubeId(url)
  if (!videoId) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border-2 border-red-500 text-red-600 hover:bg-red-50 transition-colors"
      >
        <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
        Xem video trên YouTube
      </a>
    )
  }
  return (
    <div className="rounded-2xl overflow-hidden shadow-md border border-m2-outline-variant bg-black aspect-video w-full">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title="Workshop video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    </div>
  )
}

// ─── Whiteboard Save Panel (admin only) ───────────────────────────────────────
interface WbSavePanelProps {
  workshopId: number
  onSaved: () => void
}

function WbSavePanel({ workshopId, onSaved }: WbSavePanelProps) {
  const freehandRef = useRef<FreehandWhiteboardHandle>(null)
  const [open, setOpen] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [title, setTitle] = useState(defaultWbTitle())
  const [confirmClose, setConfirmClose] = useState(false)

  const handleClose = useCallback(() => {
    if (dirty || freehandRef.current?.isDirty()) {
      setConfirmClose(true)
      return
    }
    setOpen(false)
    setDirty(false)
  }, [dirty])

  const handleSave = async () => {
    if (!freehandRef.current) return
    try {
      setSaving(true)
      const payload = await freehandRef.current.save()
      if (!payload) throw new Error('Không lấy được nội dung bảng trắng')

      const form = new FormData()
      form.append('diagramId', payload.diagramId)
      form.append('png', payload.pngBlob, `${payload.diagramId}.png`)
      form.append('xml', new Blob([payload.strokesJson], { type: 'application/json' }), `${payload.diagramId}.json`)

      const upRes = await apiClient.post('/admin/uploads/diagram', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      })
      const data = upRes.data?.data
      if (!data?.pngUrl) throw new Error(upRes.data?.message || 'Upload thất bại')

      await apiClient.post(`/admin/workshops/${workshopId}/whiteboards`, {
        title: payload.title || title,
        diagram_id: data.diagramId || payload.diagramId,
        png_url: data.pngUrl,
        xml_url: data.xmlUrl ?? null,
        is_published: true,
      })

      toast.success('Đã lưu bảng trắng')
      setOpen(false)
      setDirty(false)
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Lưu thất bại')
    } finally {
      setSaving(false)
      setConfirmClose(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => (open ? handleClose() : (setTitle(defaultWbTitle()), setDirty(false), setOpen(true)))}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-amber-500 text-white hover:bg-amber-600 shadow-sm transition-colors"
      >
        {open ? <XMarkIcon className="w-4 h-4" /> : <PencilSquareIcon className="w-4 h-4" />}
        {open ? 'Đóng bảng' : 'Viết bảng trắng'}
      </button>

      <FreehandWhiteboard
        ref={freehandRef}
        open={open}
        title={title}
        onTitleChange={setTitle}
        onDirtyChange={setDirty}
        onRequestClose={handleClose}
        initialStrokesJson={null}
        sceneKey={`workshop-${workshopId}-new`}
      />

      {confirmClose && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5 space-y-4">
            <h4 className="text-lg font-semibold text-m2-on-surface">Lưu bảng trắng?</h4>
            <p className="text-sm text-m2-on-surface-variant">
              Lưu phần viết/vẽ vừa rồi để học viên xem lại?
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg border border-m2-outline-variant hover:bg-m2-surface-low"
                onClick={() => setConfirmClose(false)}
                disabled={saving}
              >
                Tiếp tục vẽ
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                onClick={() => { setOpen(false); setDirty(false); setConfirmClose(false) }}
                disabled={saving}
              >
                Không lưu
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg bg-m2-primary text-white hover:opacity-90 disabled:opacity-50"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? 'Đang lưu…' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const WorkshopDetailPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const { user } = useAuthStore()
  const isAdmin = Boolean(user?.roles?.includes(UserRole.ADMIN))

  const [workshop, setWorkshop] = useState<WorkshopDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Viewer state
  const [activeMaterial, setActiveMaterial] = useState<WorkshopMaterial | null>(null)
  const [wbRefreshKey, setWbRefreshKey] = useState(0)

  const fetchWorkshop = useCallback(async () => {
    if (!slug) return
    try {
      const res = await apiClient.get<{ success: boolean; data: WorkshopDetail }>(
        `/public/workshops-list/${slug}`
      )
      if (res.data?.success) {
        setWorkshop(res.data.data)
        setError(null)
      } else {
        setError('Workshop không tồn tại.')
      }
    } catch {
      setError('Không tải được thông tin workshop.')
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => { void fetchWorkshop() }, [fetchWorkshop])

  if (loading) {
    return (
      <div className="min-h-screen bg-m2-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-m2-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error || !workshop) {
    return (
      <div className="min-h-screen bg-m2-background flex flex-col items-center justify-center gap-4 text-m2-on-surface-variant">
        <p className="text-lg font-medium">{error ?? 'Workshop không tồn tại.'}</p>
        <Link to="/workshops" className="text-sm text-m2-primary hover:underline">
          ← Quay lại danh sách
        </Link>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-m2-background" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* ── Hero Banner ── */}
      <section className="relative overflow-hidden border-b border-m2-outline-variant bg-gradient-to-b from-m2-surface-low to-m2-background">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgb(var(--m2-primary)/0.10) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />
        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-14">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-m2-on-surface-variant mb-6">
            <Link to="/workshops" className="hover:text-m2-primary transition-colors">
              ← Thư viện Workshop
            </Link>
            <span>/</span>
            <span className="text-m2-on-surface font-medium truncate max-w-[200px]">{workshop.title}</span>
          </nav>

          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-4 bg-m2-surface border border-m2-outline-variant shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-m2-secondary" />
            <span className="text-[10px] font-bold tracking-widest uppercase text-m2-on-surface-variant">
              Workshop đã diễn ra
            </span>
          </div>

          <h1 className="text-2xl md:text-4xl font-bold text-m2-primary mb-3 leading-tight">
            {workshop.title}
          </h1>

          {workshop.description && (
            <p className="text-sm md:text-base text-m2-on-surface-variant max-w-2xl leading-relaxed mb-4">
              {workshop.description}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm bg-m2-surface border border-m2-outline-variant text-m2-on-surface shadow-sm">
              📅 {formatDate(workshop.held_at)}
            </span>
            {workshop.duration_min && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm bg-m2-surface border border-m2-outline-variant text-m2-on-surface-variant shadow-sm">
                ⏱️ {workshop.duration_min} phút
              </span>
            )}
          </div>
        </div>
      </section>

      {/* ── Content ── */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* ── LEFT: Video + Materials + Document Viewer ── */}
          <div className="lg:col-span-2 space-y-8">
            {/* YouTube */}
            {workshop.youtube_url ? (
              <div>
                <h2 className="text-lg font-bold text-m2-primary mb-3 flex items-center gap-2">
                  🎬 Video buổi workshop
                </h2>
                <YouTubeEmbed url={workshop.youtube_url} />
              </div>
            ) : (
              <div className="rounded-2xl bg-m2-surface-low border border-dashed border-m2-outline flex flex-col items-center justify-center gap-3 py-14 text-center">
                <span className="text-4xl opacity-40">🎬</span>
                <p className="text-sm text-m2-on-surface-variant font-medium">
                  Video sẽ được cập nhật sớm
                </p>
              </div>
            )}

            {/* Tài liệu list */}
            {workshop.materials.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-m2-primary mb-4 flex items-center gap-2">
                  📄 Tài liệu đính kèm
                </h2>
                <div className="space-y-2">
                  {workshop.materials.map((m) => {
                    const isViewable = ['pdf', 'html', 'htm', 'pptx', 'ppt'].includes(m.file_type.toLowerCase())
                    const isActive = activeMaterial?.id === m.id
                    return (
                      <div
                        key={m.id}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
                          isActive
                            ? 'bg-m2-primary-container/30 border-m2-primary/30'
                            : 'bg-m2-surface border-m2-outline-variant hover:border-m2-primary/30'
                        }`}
                      >
                        <span className="text-xl shrink-0">{fileIcon(m.file_type)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-m2-on-surface truncate">{m.title}</p>
                          <p className="text-xs text-m2-on-surface-variant uppercase">{m.file_type}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isViewable && (
                            <button
                              type="button"
                              onClick={() => setActiveMaterial(isActive ? null : m)}
                              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                                isActive
                                  ? 'bg-m2-primary text-m2-on-primary'
                                  : 'bg-m2-surface-low border border-m2-outline-variant text-m2-primary hover:bg-m2-primary/10'
                              }`}
                            >
                              {isActive ? 'Đóng' : 'Xem'}
                            </button>
                          )}
                          <a
                            href={m.file_url}
                            download
                            className="inline-flex items-center gap-1 text-xs text-m2-primary hover:underline"
                            title="Tải xuống"
                          >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            Tải
                          </a>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Inline document viewer */}
                {activeMaterial && (
                  <div className="mt-4 rounded-2xl overflow-hidden border border-m2-outline-variant shadow-sm" style={{ height: 600 }}>
                    <DocumentViewer
                      fileUrl={activeMaterial.file_url}
                      fileType={activeMaterial.file_type}
                      title={activeMaterial.title}
                      canAnnotate={isAdmin}
                      workshopId={workshop.id}
                      onWhiteboardSaved={() => { setWbRefreshKey((k) => k + 1); void fetchWorkshop() }}
                      compact
                    />
                  </div>
                )}
              </div>
            )}

            {workshop.materials.length === 0 && (
              <div className="rounded-xl bg-m2-surface-low border border-dashed border-m2-outline flex items-center justify-center gap-3 py-10 text-center">
                <DocumentIcon className="w-8 h-8 text-m2-outline" />
                <p className="text-sm text-m2-on-surface-variant">Chưa có tài liệu đính kèm</p>
              </div>
            )}
          </div>

          {/* ── RIGHT: Whiteboards sidebar ── */}
          <div className="space-y-6">
            <div className="sticky top-20">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-m2-primary flex items-center gap-2">
                  🖊️ Bảng minh họa
                </h2>
                {isAdmin && (
                  <WbSavePanel
                    workshopId={workshop.id}
                    onSaved={() => { setWbRefreshKey((k) => k + 1); void fetchWorkshop() }}
                  />
                )}
              </div>

              {/* Whiteboard list */}
              <div className="space-y-3" key={wbRefreshKey}>
                {workshop.whiteboards.length === 0 && (
                  <div className="rounded-xl bg-m2-surface-low border border-dashed border-m2-outline flex flex-col items-center justify-center gap-2 py-10 text-center">
                    <span className="text-3xl opacity-30">✏️</span>
                    <p className="text-xs text-m2-on-surface-variant">
                      {isAdmin
                        ? 'Chưa có bảng minh họa. Nhấn "Viết bảng trắng" để thêm.'
                        : 'Chưa có bảng minh họa từ buổi học này.'}
                    </p>
                  </div>
                )}

                {workshop.whiteboards.map((wb) => (
                  <div key={wb.id} className="rounded-xl border border-m2-outline-variant bg-m2-surface overflow-hidden shadow-sm">
                    <div className="px-4 py-2.5 border-b border-m2-outline-variant bg-m2-surface-low flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-m2-on-surface truncate">{wb.title}</p>
                      {wb.xml_url && isAdmin && (
                        <a
                          href={wb.xml_url}
                          download
                          className="text-xs text-m2-primary hover:underline shrink-0"
                          title="Tải file JSON"
                        >
                          <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                    {wb.png_url ? (
                      <a
                        href={wb.png_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                        title="Xem toàn màn hình"
                      >
                        <img
                          src={wb.png_url}
                          alt={wb.title}
                          className="w-full h-auto object-contain max-h-64 bg-white hover:opacity-90 transition-opacity"
                        />
                      </a>
                    ) : (
                      <div className="h-24 flex items-center justify-center text-m2-on-surface-variant text-xs bg-m2-surface-low">
                        Chưa có ảnh preview
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WorkshopDetailPage
